<template>
  <div class="ugc-fabric-canvas">
    <slot></slot>
  </div>
</template>

<script>
import { fabric } from "@vfdp-customs/customs-fabric";
import { fabricCanvasHeight } from '@/data/constants';
fabric.Object.NUM_FRACTION_DIGITS = 9;

const minLimit = 0;

export default {
  name: "fabric-canvas",
  provide() {
    return {
      getCanvas: this.getCanvas
    };
  },
  props: {
    /**
     * The canvas name
     */
    name: {
      type: String,
      default: "fabCanvas"
    }
  },
  data() {
    return {
      fabricCanvas: null,
      minScaleX: null,
      maxScaleX: null,
    };
  },
  computed: {
    minPlacementSize() {
      const { activeUGCComponent,  } = this.$store.state.ugc.tempData.features[this.$store.state.ugc.tempData.activeUGCIndex];
      const {pdmModel} = this.$store.state.utils;
      const minPlacementSize = this.$store.state.utils.configuration.ugc.minPlacementSize;
      const minWidth = this.$store.state.utils.configuration.ugc.minPlacementSize;
      // Sk8-hi quarters placement is square, if that changes we can move this to the pdm
      const minHeight = pdmModel !== 'sk8-hi' && activeUGCComponent === 'quarters' ? minPlacementSize * 2 : minPlacementSize;
      return {minWidth,minHeight}
    }
  },
  methods: {
    /**
     * Function to set up the canvas selector
     * @public
     */
    getCanvas() {
      const vm = this;
      return new Promise(resolve => {
        function checkForCanvas() {
          if (vm.fabricCanvas) {
            resolve(vm.fabricCanvas);
          } else {
            setTimeout(checkForCanvas, 50);
          }
        }
        checkForCanvas();
      });
    },
    init() {
      const { tempData } = this.$store.state.ugc;
      this.setFabricCanvasSize();
      const canvas = document.createElement("canvas");
      canvas.id = this.name;
      // this solution is not resizeable this needs to be dynamic to be able to be responsive
      // the problem is that fabric uses multiple canvas elements and html5 attr for width and height to set the size
      const width = this.$el.clientWidth;
      const height = this.$el.clientHeight;
      canvas.setAttribute("width", width);
      canvas.setAttribute("height", height);
      this.$el.appendChild(canvas);
      this.fabricCanvas = new fabric.Canvas(this.name);
      this.fabricCanvas.set("selection", false); // Disable drag select
      this.fabricCanvas.defaultCursor = "move";
      this.fabricCanvas.setBackgroundColor(
        tempData.features[tempData.activeUGCIndex].background_color || (this.$store.state.utils.configuration.enableNewUgcModal ? "#f3f3f3" : "#ffffff")
      );
      this.setEvents();
      /**
       * Canvas created event.
       *
       * @property {string}
       */
      this.$emit("canvas-created", this.fabricCanvas);
    },
    /**
     * Sets listeners for mouse events inside canvas
     */
    setEvents() {
      this.fabricCanvas.on('after:render', this.onCanvasAfterRender);
      this.fabricCanvas.on('object:scaling', this.manualZoomHandler);
      this.fabricCanvas.on("mouse:down", this.mouseDownHandler);
      this.fabricCanvas.on("mouse:up", this.mouseUpHandler);
      this.onMouseWheel();
    },

    onCanvasAfterRender() {
      const canvasImage = this.fabricCanvas.getObjects()[0];

      if (canvasImage) {
        const { scaleX } = canvasImage;

        this.minScaleX = (minLimit * scaleX) / 100;
        //this.maxScaleX = scaleX * 2;
        this.fabricCanvas.off('after:render', this.onCanvasAfterRender);
      }
    },

    onMouseWheel() {
      this.fabricCanvas.on("mouse:wheel", this.mouseWheelHandler);
    },

    disableEvents() {
      this.fabricCanvas.off("mouse:wheel", this.mouseWheelHandler);
    },

    mouseWheelHandler(opt) {
      this.$eventBus.$emit("canvas:mousewheel", opt);
    },
    /**
     * Sets canvas image as active if no canvas object is clicked and starts the mouse:move fabricjs listener
     */
    mouseDownHandler({ target, pointer: mousePos }) {
      if (!target) {
        const canvasImage = this.fabricCanvas.getObjects()[0];

        // Get initial image coords
        const imagePos = {
          x: canvasImage.left,
          y: canvasImage.top
        };

        // Set canvas image as active
        this.fabricCanvas.setActiveObject(canvasImage);

        // Add event listener
        this.fabricCanvas.on("mouse:move", event =>
          this.mouseMoveHandler(event, imagePos, mousePos)
        );
      }
    },
    /**
     * Removes all mouse:move listeners on mouse:up
     */
    mouseUpHandler() {
      this.fabricCanvas.off("mouse:move");
    },
    /**
     * Sets canvas image position based on mouse:move travel distance
     */
    mouseMoveHandler(
      {
        pointer: { x: mouseMoveX, y: mouseMoveY }
      },
      { x: imageX, y: imageY },
      { x: mouseDownX, y: mouseDownY }
    ) {
      // Calculate mouse movement
      const calcX = mouseMoveX - mouseDownX;
      const calcY = mouseMoveY - mouseDownY;

      const imageObj = this.fabricCanvas.getObjects()[0];

      // Set new image position by adding mouse distance to initial coordinates
      imageObj
        .set({
          left: imageX + calcX,
          top: imageY + calcY
        })
        .setCoords();
      this.fabricCanvas.requestRenderAll();
    },
    manualZoomHandler({ target }) {
      const image = this.$store.state.ugc.stateManager.canvas.getObjects("image")[0];
      if(image.width < this.minPlacementSize.minWidth || image.height < this.minPlacementSize.minHeight ) {
        image.lockScalingX = true;
        image.lockScalingY = true;
        return
      }
      const { scaleX, type } = target;
      let imgScale = null;

      if (!type || type !== 'image') return;

      if (scaleX < this.minScaleX) {
        imgScale = this.minScaleX;
      } else if (scaleX > this.maxScaleX) {
        imgScale = this.maxScaleX;
      }

      if (imgScale) {
        target.lockUniScaling = true;
        target.lockScalingFlip = true;
        target.scale(imgScale);
        this.fabricCanvas.requestRenderAll();
      }

      if(this.$store.state.ugc.stateManager.activeControl !== "ugc-zoom-control") {
        this.$eventBus.$emit("set-active-control", "ugc-zoom-control");
      }
    },
    /**
     * Removes all fabricjs event listeners
     */
    destroyEvents() {
      this.fabricCanvas.off();
    },
    setFabricCanvasSize() {
      if (this.$store.getters['utils/noEsiActive'] && this.$store.getters['utils/isIframeSameOrigin']
        && this.$store.getters["utils/returnScreenSize"].includes('mobile')) {
        const height = window.parent.innerHeight;
        const el = document.getElementsByClassName("ugc-fabric-canvas");
        if( el.length > 0 ){
          el[0].style.height = this.getDynamicFabricCanvasHeight(height);
        }
      }
    },
    getDynamicFabricCanvasHeight(height) {
      let heightVertical = fabricCanvasHeight.vertical.default;
      if( height > 620 && height < 701 ) {
          heightVertical = fabricCanvasHeight.vertical.medium;
      } else {
        if( height > 701 && height < 800 ) {
          heightVertical = fabricCanvasHeight.vertical.large;
        } else  {
          if( height > 800) {
            heightVertical = fabricCanvasHeight.vertical.xlarge;
          }
        }
      }
    return heightVertical;
   }
  },
  mounted() {
    this.init();
  },
  created() {
    this.$eventBus.$on("disable-events-fabric-canvas", this.disableEvents);
    this.$eventBus.$on("enable-events-fabric-canvas", this.onMouseWheel);
  },
  beforeDestroy() {
    this.destroyEvents();
    this.$eventBus.$off("disable-events-fabric-canvas", this.disableEvents);
    this.$eventBus.$off("enable-events-fabric-canvas", this.onMouseWheel);
  }
};
</script>
